type Coordinate = [longitude: number, latitude: number]

interface ParcelSide {
	midpoint: Coordinate
	lengthInMeters: number
	angle: number
}

const GRS80_SEMI_MAJOR_AXIS_M = 6378137
const GRS80_ECCENTRICITY_SQUARED = 0.0066943800229
const MIN_SIDE_LENGTH_M = 3
const MIN_SIDE_SHARE_OF_PERIMETER = 0.04
const MIN_CORNER_ANGLE_DEG = 20

const radians = (degrees: number) => (degrees * Math.PI) / 180

function groundOffset(from: Coordinate, to: Coordinate): { east: number; north: number } {
	const latitude = radians((from[1] + to[1]) / 2)
	const curvature = 1 - GRS80_ECCENTRICITY_SQUARED * Math.sin(latitude) ** 2
	const meridianRadius = (GRS80_SEMI_MAJOR_AXIS_M * (1 - GRS80_ECCENTRICITY_SQUARED)) / curvature ** 1.5
	const primeVerticalRadius = GRS80_SEMI_MAJOR_AXIS_M / Math.sqrt(curvature)
	return {
		east: radians(to[0] - from[0]) * primeVerticalRadius * Math.cos(latitude),
		north: radians(to[1] - from[1]) * meridianRadius,
	}
}

function distance(from: Coordinate, to: Coordinate): number {
	const { east, north } = groundOffset(from, to)
	return Math.hypot(east, north)
}

function heading(from: Coordinate, to: Coordinate): number {
	const { east, north } = groundOffset(from, to)
	return Math.atan2(north, east)
}

function isCorner(previous: Coordinate, vertex: Coordinate, next: Coordinate): boolean {
	const turn = Math.abs(heading(previous, vertex) - heading(vertex, next)) % (2 * Math.PI)
	return Math.min(turn, 2 * Math.PI - turn) >= radians(MIN_CORNER_ANGLE_DEG)
}

function readableAngle(from: Coordinate, to: Coordinate): number {
	const angle = (-heading(from, to) * 180) / Math.PI
	if (angle > 90) return angle - 180
	if (angle < -90) return angle + 180
	return angle
}

export function parcelSides(outline: Coordinate[]): ParcelSide[] {
	const ring =
		outline.length > 1 && outline[0].every((value, index) => value === outline.at(-1)?.[index])
			? outline.slice(0, -1)
			: outline
	if (ring.length < 3) return []
	const corners = ring.filter((vertex, index) =>
		isCorner(ring[(index - 1 + ring.length) % ring.length], vertex, ring[(index + 1) % ring.length])
	)
	const perimeter = ring.reduce((total, vertex, index) => total + distance(vertex, ring[(index + 1) % ring.length]), 0)
	const minLength = Math.max(MIN_SIDE_LENGTH_M, perimeter * MIN_SIDE_SHARE_OF_PERIMETER)
	return corners
		.map((from, index) => {
			const to = corners[(index + 1) % corners.length]
			return {
				midpoint: [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2] as Coordinate,
				lengthInMeters: distance(from, to),
				angle: readableAngle(from, to),
			}
		})
		.filter(side => side.lengthInMeters >= minLength)
}
